import React, { useState, useEffect, useRef } from 'react'
import { Dropdown } from 'antd'
import InputField from '../StaticLabelInput'
import styles from './style.module.scss'
import defaultAvatar from 'assets/images/contacts/contacts-white-64.svg'
import { useTranslation } from 'react-i18next'
import editIcon from 'assets/images/contacts/Editor/profile-edit.svg'

interface ProfileProps {
  editData: any
  profileColor?: string
  onFieldChange: (field: string, value: any) => void
  onImageChange: (file: File | null) => void
}

function Profile({
  editData,
  profileColor = '#ac725e',
  onFieldChange,
  onImageChange,
}: ProfileProps) {
  const { t } = useTranslation()
  const [showNameFields, setShowNameFields] = useState({
    prefix: editData.prefix && editData.prefix.length > 0 ? true : false,
    middleName: editData.middleName && editData.middleName.length > 0 ? true : false,
    suffix: editData.suffix && editData.suffix.length > 0 ? true : false,
    phoneticFirstName:
      editData.phoneticFirstName && editData.phoneticFirstName.length > 0 ? true : false,
    phoneticLastName:
      editData.phoneticLastName && editData.phoneticLastName.length > 0 ? true : false,
    phoneticMiddleName:
      editData.phoneticMiddleName && editData.phoneticMiddleName.length > 0 ? true : false,
    nickname: editData.nickname && editData.nickname.length > 0 ? true : false,
  })

  const [nameAddItems, setNameAddItems] = useState(
    editData.isPersonal
      ? [
          { key: 'prefix', label: t('contact.property.prefix') },
          { key: 'middleName', label: t('contact.property.middleName') },
          { key: 'suffix', label: t('contact.property.suffix') },
          { key: 'phoneticFirstName', label: t('contact.property.phoneticFirstName') },
          { key: 'phoneticMiddleName', label: t('contact.property.phoneticMiddleName') },
          { key: 'phoneticLastName', label: t('contact.property.phoneticLastName') },
          { key: 'nickname', label: t('contact.property.nickname') },
        ]
      : [
          { key: 'prefix', label: t('contact.property.prefix') },
          { key: 'middleName', label: t('contact.property.middleName') },
          { key: 'suffix', label: t('contact.property.suffix') },
          { key: 'phoneticFirstName', label: t('contact.property.phoneticFirstName') },
          { key: 'phoneticLastName', label: t('contact.property.phoneticLastName') },
          { key: 'nickname', label: t('contact.property.nickname') },
        ],
  )
  // const [isPersonal, setIsPersonal] = useState(editData.isPersonal)
  const isPersonal = editData.isPersonal

  const [showCompanyFields, setShowCompanyFields] = useState({
    office: editData.office && editData.office.length > 0 ? true : false,
    phoneticCompany: editData.phoneticCompany && editData.phoneticCompany.length > 0 ? true : false,
  })
  const [companyAddItems, setCompanyAddItems] = useState([
    { key: 'office', label: t('contact.property.office') },
    { key: 'phoneticCompany', label: t('contact.property.phoneticCompany') },
  ])

  const fileInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    setNameAddItems(
      nameAddItems.filter((item) => !showNameFields[item.key as keyof typeof showNameFields]),
    )
    setCompanyAddItems(
      companyAddItems.filter(
        (item) => !showCompanyFields[item.key as keyof typeof showCompanyFields],
      ),
    )
  }, [])

  const handleImageClick = (byClick: boolean) => {
    if (byClick && !!editData.photo) {
      return
    }
    fileInputRef.current?.click()
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file && file.type.startsWith('image/')) {
      onImageChange(file)
    }
  }

  const handleImageDelete = () => {
    onImageChange(null)
  }

  const hasHideNameFields = () => {
    return Object.values(showNameFields).some((value) => !value)
  }

  const hasHideCompanyFields = () => {
    return Object.values(showCompanyFields).some((value) => !value)
  }

  const getPhotoSrc = () => {
    if (!editData.photo) {
      return null
    }
    if (typeof editData.photo === 'string') {
      if (!editData.photo.startsWith('data:image/')) {
        return 'data:image/*;base64,' + editData.photo
      }
      return editData.photo
    }
    if (editData.photo.url) {
      return editData.photo.url
    }
    return null
  }

  const photoContextMenuItems = [
    {
      key: 'pick',
      label: t('contact.menu.changeProfile'),
      onClick: () => handleImageClick(false),
    },
    {
      key: 'delete',
      label: t('contact.menu.deleteProfile'),
      onClick: handleImageDelete,
    },
  ]

  return (
    <div className={styles.profileSection}>
      <div className={styles.profileHeader}>
        <Dropdown
          menu={{ items: photoContextMenuItems }}
          trigger={['click']}
          disabled={!editData.photo}
          overlayClassName="contextMenu"
        >
          <div className={styles.profileImageContainer}>
            <div
              className={styles.profileImage}
              style={
                { '--profile-color': editData.photo ? 'none' : profileColor } as React.CSSProperties
              }
              onClick={() => handleImageClick(true)}
              role="button"
              tabIndex={0}
            >
              {editData.photo ? (
                <img src={getPhotoSrc()!} alt="Profile" className={styles.uploadedImage} />
              ) : (
                <img src={defaultAvatar} alt="profile" />
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png, image/jpeg, image/jpg"
                onChange={handleFileChange}
                className={styles.hiddenInput}
              />
            </div>
            <img
              src={editIcon}
              alt="edit"
              className={styles.profileEditIcon}
              onClick={() => handleImageClick(true)}
            />
          </div>
        </Dropdown>
        <div className={styles.profileFields}>
          <div className={styles.inputGroup}>
            <div className={styles.nameFields}>
              <div className={styles.firstNameRow}>
                {showNameFields.prefix && (
                  <InputField
                    id="prefix"
                    label={t('contact.property.prefix')}
                    value={editData.prefix}
                    onChange={(value) => onFieldChange('prefix', value)}
                    isQuarter
                  />
                )}
                <InputField
                  id="firstName"
                  label={t('contact.property.firstName')}
                  value={editData.firstName}
                  onChange={(value) => onFieldChange('firstName', value)}
                />
              </div>
              {showNameFields.middleName && (
                <InputField
                  id="middleName"
                  label={t('contact.property.middleName')}
                  value={editData.middleName}
                  onChange={(value) => onFieldChange('middleName', value)}
                />
              )}
              <div className={styles.lastNameRow}>
                <InputField
                  id="lastName"
                  label={t('contact.property.lastName')}
                  value={editData.lastName}
                  onChange={(value) => onFieldChange('lastName', value)}
                />
                {showNameFields.suffix && (
                  <InputField
                    id="suffix"
                    label={t('contact.property.suffix')}
                    value={editData.suffix}
                    onChange={(value) => onFieldChange('suffix', value)}
                    isQuarter
                  />
                )}
              </div>
              {showNameFields.phoneticFirstName && (
                <InputField
                  id="phoneticFirstName"
                  label={t('contact.property.phoneticFirstName')}
                  value={editData.phoneticFirstName}
                  onChange={(value) => onFieldChange('phoneticFirstName', value)}
                />
              )}
              {showNameFields.phoneticMiddleName && (
                <InputField
                  id="phoneticMiddleName"
                  label={t('contact.property.phoneticMiddleName')}
                  value={editData.phoneticMiddleName}
                  onChange={(value) => onFieldChange('phoneticMiddleName', value)}
                />
              )}
              {showNameFields.phoneticLastName && (
                <InputField
                  id="phoneticLastName"
                  label={t('contact.property.phoneticLastName')}
                  value={editData.phoneticLastName}
                  onChange={(value) => onFieldChange('phoneticLastName', value)}
                />
              )}
              {showNameFields.nickname && (
                <InputField
                  id="nickname"
                  label={t('contact.property.nickname')}
                  value={editData.nickname}
                  onChange={(value) => onFieldChange('nickname', value)}
                />
              )}
            </div>
            {hasHideNameFields() && (
              <Dropdown
                menu={{
                  items: nameAddItems,
                  onClick: ({ key }) => {
                    setShowNameFields({ ...showNameFields, [key]: true })
                    setNameAddItems(nameAddItems.filter((item) => item.key !== key))
                  },
                }}
                trigger={['click']}
                overlayClassName="contextMenu"
              >
                <div className={styles.addNameButton}>{t('contact.menu.addName')}</div>
              </Dropdown>
            )}
          </div>
          <div className={styles.companyFields}>
            <div className={styles.companyRow}>
              <InputField
                id="company"
                label={t('contact.property.company')}
                value={editData.company}
                onChange={(value) => onFieldChange('company', value)}
              />
              <InputField
                id="jobTitle"
                label={t('contact.property.jobTitle')}
                value={editData.jobTitle}
                onChange={(value) => onFieldChange('jobTitle', value)}
              />
            </div>
            <div className={styles.departmentRow}>
              <InputField
                id="department"
                label={t('contact.property.department')}
                value={editData.department}
                onChange={(value) => onFieldChange('department', value)}
              />
              {showCompanyFields.office && (
                <InputField
                  id="office"
                  label={t('contact.property.office')}
                  value={editData.office}
                  onChange={(value) => onFieldChange('office', value)}
                />
              )}
            </div>
            {showCompanyFields.phoneticCompany && (
              <InputField
                id="phoneticCompany"
                label={t('contact.property.phoneticCompany')}
                value={editData.phoneticCompany}
                onChange={(value) => onFieldChange('phoneticCompany', value)}
              />
            )}
            {!isPersonal && hasHideCompanyFields() && (
              <Dropdown
                menu={{
                  items: companyAddItems,
                  onClick: ({ key }) => {
                    setShowCompanyFields({ ...showCompanyFields, [key]: true })
                    setCompanyAddItems(companyAddItems.filter((item) => item.key !== key))
                  },
                }}
                trigger={['click']}
                overlayClassName="contextMenu"
              >
                <div className={styles.addOrgButton}>{t('contact.menu.addOrg')}</div>
              </Dropdown>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default Profile
